import { TextField, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import FormMonitoring from "components/FormMonitoring";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppColors } from "resources/AppColors";
import CustomDialog from "components/Dialogs/MasterDialog";
import { useLocation, useNavigate } from "react-router-dom";
import { CommonErrorsTexts, packagesTexts } from "locale/es";
import { z } from "zod";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { initializeWizards } from "redux/reducers/wizardsReducer";
import SingleChoiceDropdown from "components/Dropdowns/SingleChoiceDropdown";

const useStyles = makeStyles((theme) => ({
  textFieldLabel: {
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: AppColors.PRIMARY,
      },
    },
    "& .MuiInputBase-root": {
      color: AppColors.PRIMARY,
    },
    "& .MuiInputLabel-root": {
      color: AppColors.PRIMARY,
    },
    color: AppColors.PRIMARY,
    backgroundColor: AppColors.BOX_FILL,
  },
}));

const physicalActivityTypeOptions = [
  { name: 'VERSION3', value: 'VERSION3' },
  { name: 'VERSION3_NEUTRAL', value: 'VERSION3_NEUTRAL' },
]

const newPackageSchema = z
  .object({
    name: z
      .string({ required_error: CommonErrorsTexts.required })
      .min(2, packagesTexts.formInputErrors.name),
    wizards: z.string().optional(),
    physicalActivityType: z.string().optional()
  })
  .transform((data) => ({
    ...data,
    status: "ACTIVE",
    id: null,
    wizards: data.wizards ? [data.wizards] : [],
    physicalActivityType: data.physicalActivityType ? data.physicalActivityType : null,
  }));

function PackageForm({ onSubmit, isEdit }) {
  const { entity } = useLocation().state;

  const entityId = entity?.id;

  const navigate = useNavigate();

  const wizardsData = useSelector(({ wizards }) => wizards.data);

  const [formError, setFormError] = useState("");

  const selectedCenterId = useSelector(({ center }) => center)?.data?.id;

  const classes = useStyles();

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(initializeWizards());
  }, [dispatch]);

  const { handleSubmit, control, reset } = useForm({
    mode: "onBlur",
    reValidateMode: "onChange",
    resolver: zodResolver(newPackageSchema),
    resetOptions: {
      keepDefaultValues: true,
    },
    defaultValues: {
      name: entity?.name || "",
      status: entity?.status || "",
      wizards:
        entity?.wizards && entity?.wizards[0]?.title.length > 0
          ? entity.wizards[0].title
          : "",
        physicalActivityType: entity?.physicalActivityType || ""
    },
  });

  async function handleOnConfirmSubmit(data) {
    try {
      const selectedWizard = wizardsData.find(
        (wizard) => wizard.name === data.wizards[0]
      );
      const { name, value, ...wizardWithoutNameAndValue } =
        selectedWizard || {};

      const packageObject = {
        ...data,
        wizards: selectedWizard ? [wizardWithoutNameAndValue] : [],
        id: isEdit ? entityId : null,
      };

      if (isEdit) {
        await dispatch(onSubmit(entityId, packageObject));
      } else {
        await dispatch(onSubmit(selectedCenterId, packageObject));
      }
      handleShowAddModal();
    } catch (error) {
      setFormError(error);
    }
  }

  function handleShowAddModal() {
    navigate("/paquetes");
  }

  return (
    <CustomDialog
      title={packagesTexts.CREATE}
      show={true}
      handleShow={handleShowAddModal}
    >
      <FormMonitoring
        submitForm={handleSubmit(handleOnConfirmSubmit)}
        resetFormInputs={reset}
        isEdit={isEdit}
      >
        <Controller
          control={control}
          name='name'
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              type='text'
              InputLabelProps={{
                required: true,
              }}
              label={packagesTexts.NAME}
              margin='normal'
              variant='outlined'
              multiline
              autoFocus
              className={classes.textFieldLabel}
              error={Boolean(error)}
              helperText={error?.message ?? ""}
            />
          )}
        />
        <Controller
          control={control}
          name='wizards'
          render={({ field: { onChange, onBlur, value } }) => (
            <SingleChoiceDropdown
              label={packagesTexts.WIZARD}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              items={wizardsData}
            />
          )}
        />
        <Controller
          control={control}
          name='physicalActivityType'
          render={({ field: { onChange, onBlur, value } }) => (
            <SingleChoiceDropdown
              label={packagesTexts.PHYSICALACTIVITY}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              items={physicalActivityTypeOptions}
            />
          )}
        />
        <Typography variant='body' sx={{ color: "red" }}>
          {formError && packagesTexts.formInputErrors.api_error}
        </Typography>
      </FormMonitoring>
    </CustomDialog>
  );
}

export default PackageForm;
